import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Activity } from "lucide-react";
import { useT } from "@/lib/i18n";
import { api } from "@/lib/api";

interface IntegrationHealth {
  integration: string;
  display_name: string;
  status: "healthy" | "degraded" | "down" | "unknown";
  errors_30min: number;
  errors_24h: number;
  success_rate_24h: number;
  last_error_message: string | null;
}

interface HealthResponse {
  rollup: "healthy" | "degraded" | "down";
  integrations: IntegrationHealth[];
}

const DOT_COLOR: Record<HealthResponse["rollup"] | "unknown", string> = {
  healthy: "bg-emerald-500",
  degraded: "bg-amber-500",
  down: "bg-red-500",
  unknown: "bg-gray-400",
};

/**
 * Top-nav badge. Polls /admin/integrations/health every 60s and lights the
 * dot green / amber / red based on the worst-case integration status in
 * the last 24h. Click navigates to the full /integrations page.
 */
export function IntegrationStatusBadge() {
  const navigate = useNavigate();
  const t = useT();

  const { data, isError } = useQuery({
    queryKey: ["admin", "integrations", "health"],
    queryFn: () => api<HealthResponse>("/api/v1/admin/integrations/health"),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const rollup = isError ? "unknown" : (data?.rollup ?? "unknown");
  const tooltipKey =
    rollup === "healthy"
      ? "topnav.integrationStatusHealthy"
      : rollup === "degraded"
        ? "topnav.integrationStatusDegraded"
        : rollup === "down"
          ? "topnav.integrationStatusDown"
          : "topnav.integrationStatusUnknown";
  const tooltipFallback =
    rollup === "healthy"
      ? "All integrations healthy"
      : rollup === "degraded"
        ? "Some integrations are degraded"
        : rollup === "down"
          ? "An integration is down"
          : "Integration status unknown";

  return (
    <button
      onClick={() => navigate("/integrations")}
      className="relative flex items-center gap-1.5 rounded-md px-2 py-1 text-sm text-muted-foreground transition-colors hover:bg-accent"
      title={t(tooltipKey, tooltipFallback)}
    >
      <Activity className="h-4 w-4" />
      <span className={`h-2 w-2 rounded-full ${DOT_COLOR[rollup]}`} />
    </button>
  );
}
